import { useEffect, useState } from "react";
import { REVERSE_MAPING_BADGE_TYPE, ICON_COMPLETE_MAP } from "../../RnRCommonComponent/CoreBadge/rewardsConstants";
import { useRnRCommonService } from "../../RnRCommonComponent/service/RnRCommon.service.jsx";
import { useManageRewardsService } from "../../manageRewards/services/manageRewards.service.jsx";
import { useNominatedRewardsService } from "../service/NominatedRewards.service.jsx";
import gql from 'graphql-tag';

const QUERY_BADGES_BY_COMPANY_ID = gql`
  query QueryBadgesByCompanyIdIndex($companyId: ID!, $limit: Int, $nextToken: String) {
    queryBadgesByCompanyIdIndex(companyId: $companyId, limit: $limit, nextToken: $nextToken) {
      items {
        id
        companyId
        badgeTypeId
        assignmentSSORequiredId
        recipientPayTypeId
        recipientSSORequiredId
        physicalAwardActivityId
        iconId
        snappyGrantId
        snappyGrant {
          id
          snappyCampaignId
          name
          description
          costInCurrency
        }
        name
        label
        description
        image
        color
        gradientColor
        points
        bonusPoints
        rewardBonus
        rewardMaxBonus
        assignmentLimit
        assignmentLevelId
        recipientLevelId
        isPreApproved
        isRewardRequiresDate
        visibility
        isAssignmentSSORequired
        isRecipientSSORequired
        automationRule
        externalWorkFlowId
      }
      nextToken
    }
  }
`;

const CREATE_NOMINATION_CONFIG = gql`mutation CreateNominationConfiguration($input: NominationConfigurationInput!) {
    createNominationConfiguration(input: $input) {
        id
        createdAt
        startDate
        status
        updatedAt
        badgeId
        companyId
        configId
        endDate
        awardedCount
        nominationConfigurationId
        nominatorEmployeeLevelId
        nominatorEmployeePayTypeId
        nominatorEmployeelogInTypeId
        nomineeEmployeeLevelId
        nomineeEmployeePayTypeId
        nomineeEmployeelogInTypeId
        snappyCampaignTierId
        snappyGrantId
    }
}`

const useManageNominatedReward = (props = {}) => {
    const { client, currentUser } = props;
    const [badgeList, setBadgeList] = useState([])
    const [empLoginType, setEmpLoginType] = useState([])
    const [empPayType, setEmpPayType] = useState([])
    const [empLevel, setEmpLevel] = useState([])
    const [showLoader, setShowLoader] = useState({
        badgeList: false,
        empLoginType: false,
        empPayType: false,
        empLevel: false,
    })
    const commonAPIService = useRnRCommonService();
    const manageRewardsService = useManageRewardsService();
    const nominatedAPIService = useNominatedRewardsService();

    const getAllBadgeList = async () => {
        setShowLoader({
            badgeList: true,
            empLoginType: true,
            empPayType: true,
            empLevel: true,
        });
        
        try {
            if (!client) {
                console.error('Apollo client not available');
                setShowLoader({
                    ...showLoader,
                    badgeList: false
                });
                return;
            }
            
            const companyId = currentUser?.companyId || currentUser?.company?.id;
            
            if (!companyId) {
                console.warn('Company ID not found in currentUser:', currentUser);
                setBadgeList([]);
                setShowLoader(prev => ({
                    ...prev,
                    badgeList: false
                }));
                return;
            }
            
            const { data, error } = await client.query({
                query: QUERY_BADGES_BY_COMPANY_ID,
                variables: {
                    companyId: companyId,
                    limit: 100,
                    nextToken: null
                },
                fetchPolicy: 'network-only'
            });
            
            if (error) {
                console.error(error);
                setShowLoader({
                    ...showLoader,
                    badgeList: false
                });
                return;
            }
            
            if (data?.queryBadgesByCompanyIdIndex?.items) {
                const enrichedBadges = data.queryBadgesByCompanyIdIndex.items.map(badge => {
                    const badgeTypeCode = REVERSE_MAPING_BADGE_TYPE[badge.badgeTypeId] || '';
                    
                    let iconInfo = null;
                    if (badge.iconId) {
                        iconInfo = ICON_COMPLETE_MAP.find(icon => icon.id === badge.iconId);
                    }
                    
                    return {
                        ...badge,
                        badgeType: badgeTypeCode,
                        iconInfo
                    };
                });
                
                const filteredBadges = enrichedBadges.filter(badge =>
                    badge.badgeType === "NOMINATED" &&
                    (badge.visibility !== false)
                );
                setBadgeList(filteredBadges);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setShowLoader({
                ...showLoader,
                badgeList: false
            });
        }

        manageRewardsService.getEmployeeLevels().then((response) => {
            if (response?.code === 200) {
                if (response?.data) {
                    setEmpLevel(response.data)
                }
            } else {
            }
        }).catch((e) => {
            console.error(e);
        }).finally(() => {
            setShowLoader({
                ...showLoader,
                empLevel: false
            })
        });

        nominatedAPIService.getEmployeeLoginType().then((response) => {
            if (response?.code === 200) {
                if (response?.data) {
                    setEmpLoginType(response.data)
                }
            }
        }).catch((e) => {
            console.error(e);
        }).finally(() => {
            setShowLoader({
                ...showLoader,
                empLoginType: false
            })
        });

        manageRewardsService.getEmployeePayType().then((response) => {
            if (response?.code === 200) {
                if (response?.data) {
                    setEmpPayType(response.data)
                }
            }
        }).catch((e) => {
           console.error(e);
        }).finally(() => {
            setShowLoader({
                ...showLoader,
                empPayType: false
            })
        });
    };

    const createNominationConfiguration = async (values) => {
        const nominationCriteria = values?.nominationCriteria;
        let payload = {
            companyId: currentUser?.companyId,
            badgeId: values.badgeId,
            startDate: values.startDate,
            endDate: values.endDate,
            awardedCount: parseInt(nominationCriteria?.nominator?.awardedCount || "1")
        };
        if (nominationCriteria?.nominator?.employeeLevelId?.length > 0) {
            payload.nominatorEmployeeLevelId = nominationCriteria.nominator.employeeLevelId;
        }
        if (nominationCriteria?.nominee?.employeeLevelId?.length > 0) {
            payload.nomineeEmployeeLevelId = nominationCriteria.nominee.employeeLevelId;
        }
        if (new Date(payload.startDate) <= new Date()) {
            payload.status = 'Open';
        } else {
            payload.status = 'Pending';
        }
        if (nominationCriteria?.nominee?.employeelogInTypeId) {
            payload.nomineeEmployeelogInTypeId = nominationCriteria.nominee.employeelogInTypeId;
        }
        if (nominationCriteria?.nominee?.employeePayTypeId) {
            payload.nomineeEmployeePayTypeId = nominationCriteria.nominee.employeePayTypeId;
        }
        if (nominationCriteria?.nominator?.employeelogInTypeId) {
            payload.nominatorEmployeelogInTypeId = nominationCriteria.nominator.employeelogInTypeId;
        }
        if (nominationCriteria?.nominator?.employeePayTypeId) {
            payload.nominatorEmployeePayTypeId = nominationCriteria.nominator.employeePayTypeId;
        }
        let configId = null;
        try {
            const { data, error } = await client.mutate({
                mutation: CREATE_NOMINATION_CONFIG,
                variables: {
                    input: {...payload}
                }
            });
            if (error) throw error;
            configId = data?.createNominationConfiguration?.id || data?.createNominationConfiguration?.configId || data?.createNominationConfiguration?.nominationConfigurationId;
        } catch (e) {
            console.log(e);
        }
        const nominationConfigObject = {
            id: configId || values?.id,
            badgeId: values?.badgeId,
            startDate: values?.startDate,
            endDate: values?.endDate,
            status: values?.status,
            awardedCount: parseInt(values?.nominationCriteria?.nominator?.awardedCount),
            nomineeEmployeeLevelId: values?.nominationCriteria?.nominee?.employeeLevelId,
            nomineeEmployeelogInTypeId: values?.nominationCriteria?.nominee?.employeelogInTypeId,
            nomineeEmployeePayTypeId: values?.nominationCriteria?.nominee?.employeePayTypeId,
            nominatorEmployeeLevelId: values?.nominationCriteria?.nominator?.employeeLevelId,
            nominatorEmployeelogInTypeId: values?.nominationCriteria?.nominator?.employeelogInTypeId,
            nominatorEmployeePayTypeId: values?.nominationCriteria?.nominator?.employeePayTypeId,
        }
        return nominatedAPIService.createNominationConfiguration(nominationConfigObject);
    }

    const getNominationConfigurationById = (id) => {
        return commonAPIService.getNominationConfigurationById(id);
    }

    useEffect(() => {
        getAllBadgeList();
    }, [currentUser?.companyId]);

    return ({
        empLevel,
        badgeList,
        empLoginType,
        empPayType,
        loading: Object.values(showLoader).flat().includes(true),
        createNominationConfiguration,
        getNominationConfigurationById
    })
}

export default useManageNominatedReward;
